import { useEffect, useState } from "react";
import { captureException } from "src/lib/monitoring";
import { useFlags } from "launchdarkly-react-client-sdk";

import { queryStatusPage } from "src/utils/query-status-page";
import { formatFriendlyDistanceToNow } from "src/utils/time";
import { load, save } from "src/utils/storage";

export type StatusPageIncident = {
  id: string;
  title: string;
  message: string;
};

export const useIncidentBanner = (): {
  loading: boolean;
  incident?: StatusPageIncident;
  onDismiss: () => void;
} => {
  const { incidentBanner } = useFlags();
  const [incident, setIncident] = useState<StatusPageIncident | undefined>();
  const [loading, setLoading] = useState(false);
  const [lastBannerDismissed, setLastBannerDismissed] = useState(() =>
    load("app-incident-banner"),
  );

  async function fetchData() {
    try {
      setLoading(true);
      const statusPageData = await queryStatusPage();

      const unresolvedIncident = statusPageData?.incidents?.[0];
      const recentUpdate = unresolvedIncident?.incident_updates?.[0];

      if (unresolvedIncident && recentUpdate) {
        const relativeUpdateTime = formatFriendlyDistanceToNow(
          recentUpdate.updated_at,
        );

        setIncident({
          title: unresolvedIncident.name,
          message: `${recentUpdate.body} (Last update ${relativeUpdateTime})`,
          id: unresolvedIncident.id,
        });
      }

      setLoading(false);
    } catch (error) {
      captureException(error);
      setLoading(false);
    }
  }

  const dismissBanner = () => {
    if (incident?.id) {
      save("app-incident-banner", incident.id);
      setLastBannerDismissed(incident?.id);
    }
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    fetchData();

    // Re-fetch the data every 5 minutes in the event the user does't refresh the page, and an outage occurs.
    const interval = setInterval(() => {
      fetchData();
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  return {
    loading,
    incident:
      incidentBanner && lastBannerDismissed !== incident?.id
        ? incident
        : undefined,
    onDismiss: dismissBanner,
  };
};
